'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import * as T from 'prop-types';
import React, {Fragment} from 'react';
import {CommentIndicator} from '../../../comments/components/CommentIndicator';
import {Button} from '../../../components/buttons/Button';
import {QuickStatusButton} from '../../../components/buttons/QuickStatusButton';
import {StatelessStatusSelector} from '../../../core-components/selectors/components/StatelessStatusSelector';
import {getAsString, getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {isButtonState} from '../../../core-utilities/enums/ButtonState';
import {getMessagesFromProps, I18nComponent} from '../../../core-utilities/react-i18n';
import {stopPropagation} from '../../../core-utilities/react-utils';
import {isWebBuild} from '../../../config/constants';


export function BaseRequirementViewBannerView(props) {
  const requirement = props.requirement;
  const description = get(requirement, 'description');
  const uploadingStatus = props.uploadingStatus;
  const hasInProgress = get(uploadingStatus, 'hasInProgress', false);
  const hasPending = get(uploadingStatus, 'hasPending', false);
  const showRetryUploads = props.canRetryUploads && hasPending;
  const countIsEnforced = get(requirement, 'countIsEnforced', '-');
  const countIsEnforcedValue = countIsEnforced === '-'
    ? '-'
    : (countIsEnforced ? props.yesLabel : props.noLabel);

  // stopPropagation on clicks here for placeholder click-to-open-camera functionality of the view.
  return (
    <div
      className={classNames("RequirementViewBanner", props.className)}
      onClick={stopPropagation}
    >
      <div className="RequirementViewBanner__header">
        <h1 className="RequirementViewBanner__headerLabel">
          {getAsString(requirement)}
        </h1>

        <div className="RequirementViewBanner__statusSection">
          <span className="RequirementViewBanner__statusLabel">
            {`${props.statusLabel}:`}
          </span>
          {requirement ? (
            <StatelessStatusSelector
              className="RequirementViewBanner__status"
              labelClassName="RequirementViewBanner__status__label"
              model={requirement}
              hideValue={false}
              hideIcon={true}

              status={requirement.status}
              collection={requirement.collection}
              validStatuses={requirement.validStatuses}
            />
          ) : null}
          <CommentIndicator
            className="RequirementViewBanner__chat"
            titleClassName="RequirementViewBanner__chatTitle"
            displayTitle={true}
            title={props.chatTitle}
            userId={props.userId}
            chatId={getId(requirement)}
            collection={getCollection(requirement)}
            allowNav={false}

            init={props.init}
            {...getMessagesFromProps(props, CommentIndicator.messageProps)}
          />
          <Button
            className="RequirementViewBanner__editButton"
            label={props.editLabel}
            onClick={props.onClickEdit}
          />
        </div>

        {description && (
          <Fragment>
            <span className="LabeledBlock__label">
              Description
            </span>
            <span className={classNames("RequirementViewBanner__description", {
              RequirementViewBanner__description_collapsed: !props.isHeaderExpanded,
              RequirementViewBanner__description_expanded: props.isHeaderExpanded,
            })}
            >
              {description}
            </span>
          </Fragment>
        )}

        <div className="RequirementViewBanner__wrapper">
          <div className="RequirementViewBanner__fileProps">
            <span className="RequirementViewBanner__propLabel">
              {`${props.minimumFileCountLabel}:`}
            </span>
            <span className="RequirementViewBanner__propValue">
              {get(requirement, 'minimumFileCount', '-')}
            </span>
            <span className="RequirementViewBanner__propLabel">
              {`${props.maximumFileCountLabel}:`}
            </span>
            <span className="RequirementViewBanner__propValue">
              {get(requirement, 'maximumFileCount', '-')}
            </span>
            <span className="RequirementViewBanner__propLabel RequirementViewBanner__fileProps__enforced">
              {`${props.countIsEnforcedLabel}:`}
            </span>
            <span className="RequirementViewBanner__propValue RequirementViewBanner__fileProps__enforced">
              {countIsEnforcedValue}
            </span>
          </div>

          {description && (
            <Button
              className="RequirementViewBanner__expandButton"
              label={props.isHeaderExpanded ? props.collapseLabel : props.expandLabel}
              onClick={props.onToggleHeaderExpanded}
            />
          )}
        </div>
      </div>

      <div className="RequirementViewBanner__headerSubPanel">
        <QuickStatusButton
          className={classNames("RequirementViewBanner__quickButton", {
            'hide-elem': showRetryUploads,
          })}
          disabled={hasInProgress || hasPending}
          model={requirement}
          status={get(requirement, 'status')}
          validStatuses={get(requirement, 'validStatuses')}
        />
        {showRetryUploads ? (
          <Button
            className="RequirementViewBanner__retryButton"
            label={props.retryLabel}
            onClick={props.onRetryUploads}
          />
        ) : null}
        {props.showUploadButton ? (
          <Button
            className="RequirementViewBanner__iconButton"
            label={props.uploadLabel}
            labelClass="RequirementViewBanner__iconButton__label"
            leftIconClass="RequirementViewBanner__iconButton__icon icon ion-android-upload"
            disabled={!props.canAddFile && props.isWebBuild}
            onClick={props.canAddFile ? props.onOpenFilePicker : props.onDisabledAttempt}
          />
        ) : null}
        {props.showIOSUpButton ? (
          <Button
            className="RequirementViewBanner__iconButton"
            label={props.uploadIOSLabel}
            labelClass="RequirementViewBanner__iconButton__label"
            leftIconClass="RequirementViewBanner__iconButton__icon icon ion-ios-photos"
            disabled={!props.canAddFile && props.isWebBuild}
            onClick={props.canAddFile ? props.onOpenIOSFilePicker : props.onDisabledAttempt}
          />
        ) : null}
        {props.showDownloadButton ? (
          <Button
            className="RequirementViewBanner__iconButton RequirementViewBanner__downloadButton"
            label={props.downloadTitle}
            labelClass="RequirementViewBanner__iconButton__label"
            leftIconClass="RequirementViewBanner__iconButton__icon icon ion-android-download"
            disabled={!props.canDownloadFiles}
            onClick={props.onDownloadAllFiles}
          />
        ) : null}
        {props.showCameraButton ? (
          <Button
            className="RequirementViewBanner__iconButton"
            label={props.cameraLabel}
            labelClass="RequirementViewBanner__iconButton__label"
            leftIconClass="RequirementViewBanner__iconButton__icon icon ion-ios-camera"
            disabled={!props.canAddFile && props.isWebBuild}
            onClick={props.canAddFile ? props.onOpenCamera : props.onDisabledAttempt}
          />
        ) : null}
        {props.showVideoButton ? (
          <Button
            className="RequirementViewBanner__iconButton"
            label={props.videoLabel}
            labelClass="RequirementViewBanner__iconButton__label"
            leftIconClass="RequirementViewBanner__iconButton__icon icon ion-ios-videocam"
            disabled={!props.canAddFile && props.isWebBuild}
            onClick={props.canAddFile ? props.onOpenVideo : props.onDisabledAttempt}
          />
        ) : null}
      </div>
    </div>
  );
}

BaseRequirementViewBannerView.propTypes = {
  className: T.string,
  isHeaderExpanded: T.bool,
  userId: T.string.isRequired,
  requirement: T.object,
  showCameraButton: T.bool,
  showVideoButton: T.bool,
  showUploadButton: T.bool,
  showIOSUpButton: T.bool,
  showDownloadButton: T.bool,
  canAddFile: T.bool,
  canRetryUploads: T.bool,
  canDownloadFiles: T.bool,
  showQuickSubmit: T.bool,
  showQuickApprove: T.bool,
  quickMode: isButtonState,
  uploadingStatus: T.any,

  onClickEdit: T.func.isRequired,
  onToggleHeaderExpanded: T.func.isRequired,
  onOpenCamera: T.func.isRequired,
  onOpenVideo: T.func.isRequired,
  onOpenFilePicker: T.func.isRequired,
  onOpenIOSFilePicker: T.func.isRequired,
  onDownloadAllFiles: T.func.isRequired,
  onDisabledAttempt: T.func.isRequired,
  onRetryUploads: T.func,

  statusLabel: T.string,
  editLabel: T.string,
  uploadLabel: T.string,
  uploadIOSLabel: T.string,
  cameraLabel: T.string,
  videoLabel: T.string,
  retryLabel: T.string,
  downloadTitle: T.string,
  collapseLabel: T.string,
  expandLabel: T.string,
  minimumFileCountLabel: T.string,
  maximumFileCountLabel: T.string,
  countIsEnforcedLabel: T.string,
  yesLabel: T.string,
  noLabel: T.string,

  init: T.bool,
};

BaseRequirementViewBannerView.defaultProps = {
  init: true,
  isWebBuild: isWebBuild(),
};

export const RequirementViewBannerView = I18nComponent(BaseRequirementViewBannerView, [
  {code: 'status.label', prop: 'statusLabel'},
  {code: 'default.button.edit.label', prop: 'editLabel'},
  {code: 'requirements.files.buttons.camera.label', prop: 'cameraLabel'},
  {code: 'requirements.files.buttons.video.label', prop: 'videoLabel'},
  {code: 'requirements.files.buttons.upload.label', prop: 'uploadLabel'},
  {code: 'requirements.files.buttons.upload.label.ios', prop: 'uploadIOSLabel'},
  {code: 'requirements.files.buttons.download.label.short', prop: 'downloadTitle'},
  {code: 'default.button.collapse.label', prop: 'collapseLabel'},
  {code: 'default.button.expand.label', prop: 'expandLabel'},
  {code: 'requirements.files.context.retyuploads.label', prop: 'retryLabel'},
  {code: 'requirements.files.details.min.label.short', prop: 'minimumFileCountLabel'},
  {code: 'requirements.files.details.max.label.short', prop: 'maximumFileCountLabel'},
  {code: 'requirements.files.details.enforced.label', prop: 'countIsEnforcedLabel'},
  {code: 'default.yes.label', prop: 'yesLabel'},
  {code: 'default.no.label', prop: 'noLabel'},
  {code: 'requirements.views.chat.title', prop: 'chatTitle'},
  ...CommentIndicator.messages,
]);



// WEBPACK FOOTER //
// ./src/js/requirements/components/RequirementViewBanner/RequirementViewBannerView.js